// Spec 38. Stable, non-overlapping community-aware layout.
//
// Communities already exist in the payload: the `community` worker job stores
// each Louvain cluster as a `community`-type node (here under the `_type`
// attribute, since sigma reserves `type`) with a `member_of` edge from every
// member. We read that (no data change) and lay the graph out so nodes never
// stack, at any zoom.
//
// The whole layout is expressed in units commensurate with node SIZE, with a
// bounded total extent — see specs/renketsu-v2/38. That's the fix for the
// stacking: sigma sizes nodes in screen pixels while distances scale with the
// camera, so the only way to guarantee separation is to keep node size,
// member spacing, community size, and overall extent on one consistent scale.

import Graph from "graphology";
import forceAtlas2 from "graphology-layout-forceatlas2";

const TAU = Math.PI * 2;
const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));

function isCommunityNode(g: Graph, id: string): boolean {
	// `_type`, not `type`: `type` is reserved by sigma to pick a node program.
	return g.getNodeAttribute(id, "_type") === "community";
}

function maxNodeSize(g: Graph): number {
	let max = 1;
	g.forEachNode((_id, a) => {
		const s = a.size;
		if (typeof s === "number" && s > max) max = s;
	});
	return max;
}

function plainForceAtlas(g: Graph): void {
	const n = g.order;
	forceAtlas2.assign(g, {
		iterations: n > 500 ? 100 : 180,
		settings: {
			gravity: 1,
			scalingRatio: 10,
			linLogMode: true,
			strongGravityMode: false,
			barnesHutOptimize: n > 500,
			slowDown: 5,
		},
	});
}

// Deterministic circle-overlap resolver: treat each node as a disc of radius
// = its `size` attribute and push overlapping pairs apart until none overlap
// (or `iters` reached). Used on the community meta-graph so communities are
// guaranteed not to overlap regardless of where ForceAtlas2 left them.
function resolveCircleOverlaps(
	graph: Graph,
	margin: number,
	iters: number,
): void {
	const ids = graph.nodes();
	for (let it = 0; it < iters; it++) {
		let moved = false;
		for (let i = 0; i < ids.length; i++) {
			const A = ids[i]!;
			const ax = graph.getNodeAttribute(A, "x") as number;
			const ay = graph.getNodeAttribute(A, "y") as number;
			const ra = graph.getNodeAttribute(A, "size") as number;
			for (let j = i + 1; j < ids.length; j++) {
				const B = ids[j]!;
				const bx = graph.getNodeAttribute(B, "x") as number;
				const by = graph.getNodeAttribute(B, "y") as number;
				const rb = graph.getNodeAttribute(B, "size") as number;
				let dx = bx - ax;
				let dy = by - ay;
				let d = Math.hypot(dx, dy);
				const minD = ra + rb + margin;
				if (d >= minD) continue;
				if (d < 1e-6) {
					// Coincident: shove along a deterministic angle.
					dx = Math.cos(i * GOLDEN_ANGLE);
					dy = Math.sin(i * GOLDEN_ANGLE);
					d = 1;
				}
				const push = (minD - d) / 2;
				const ux = dx / d;
				const uy = dy / d;
				graph.setNodeAttribute(A, "x", ax - ux * push);
				graph.setNodeAttribute(A, "y", ay - uy * push);
				graph.setNodeAttribute(B, "x", bx + ux * push);
				graph.setNodeAttribute(B, "y", by + uy * push);
				moved = true;
			}
		}
		if (!moved) break;
	}
}

export function assignCommunityLayout(g: Graph): void {
	// 1. Membership.
	const communityOf = new Map<string, string>();
	g.forEachNode((id) => {
		if (isCommunityNode(g, id)) communityOf.set(id, id);
	});
	g.forEachEdge((_e, attrs, source, target) => {
		if (attrs.relation === "member_of" && isCommunityNode(g, target)) {
			communityOf.set(source, target);
		}
	});

	const communities = [...new Set(communityOf.values())].filter((c) =>
		isCommunityNode(g, c),
	);

	if (communities.length < 2) {
		plainForceAtlas(g);
		g.forEachNode((id) => g.setNodeAttribute(id, "_community", null));
		return;
	}

	const groups = new Map<string, string[]>();
	for (const c of communities) groups.set(c, []);
	for (const [node, c] of communityOf) {
		if (node !== c) groups.get(c)?.push(node);
	}

	// 2. One consistent scale, derived from node size. `S` is the member
	//    spacing step; everything else is a multiple of it.
	const maxSize = maxNodeSize(g);
	const S = Math.max(maxSize * 1.8, 8);
	const radiusFor = (count: number) =>
		S * Math.sqrt(Math.max(count, 1)) + maxSize * 1.5;

	// 3. Meta-graph: super-nodes sized by their community radius, packed by
	//    ForceAtlas2 with adjustSizes (so they don't overlap) at a scale
	//    proportional to content — NOT an arbitrary 200x spread.
	const meta = new Graph({ type: "undirected" });
	const seedR = S * Math.sqrt(communities.length);
	communities.forEach((c, i) => {
		const a = (TAU * i) / communities.length;
		meta.addNode(c, {
			x: Math.cos(a) * seedR,
			y: Math.sin(a) * seedR,
			size: radiusFor(groups.get(c)?.length ?? 0),
		});
	});
	g.forEachEdge((_e, attrs, s, t) => {
		if (attrs.relation === "member_of") return;
		const cs = communityOf.get(s);
		const ct = communityOf.get(t);
		if (!cs || !ct || cs === ct) return;
		if (meta.hasEdge(cs, ct)) {
			meta.updateEdgeAttribute(cs, ct, "weight", (w?: number) => (w ?? 1) + 1);
		} else {
			meta.addEdge(cs, ct, { weight: 1 });
		}
	});
	forceAtlas2.assign(meta, {
		iterations: communities.length > 200 ? 400 : 250,
		settings: {
			gravity: 1,
			scalingRatio: 10,
			adjustSizes: true,
			edgeWeightInfluence: 1,
			strongGravityMode: false,
			barnesHutOptimize: communities.length > 200,
			slowDown: 6,
		},
	});
	// Make community separation exact (FA2 leaves residual overlaps).
	resolveCircleOverlaps(meta, S * 0.6, 150);

	// 4. Members: phyllotaxis around the centroid, step `S`. Sunflower
	//    nearest-neighbour distance ~1.7·S > 2·maxNodeSize, so discs never
	//    overlap.
	for (const c of communities) {
		const cx = meta.getNodeAttribute(c, "x") as number;
		const cy = meta.getNodeAttribute(c, "y") as number;
		g.setNodeAttribute(c, "x", cx);
		g.setNodeAttribute(c, "y", cy);
		g.setNodeAttribute(c, "_community", c);

		const members = groups.get(c) ?? [];
		members.forEach((m, i) => {
			const r = S * Math.sqrt(i + 0.5);
			const a = i * GOLDEN_ANGLE;
			g.setNodeAttribute(m, "x", cx + Math.cos(a) * r);
			g.setNodeAttribute(m, "y", cy + Math.sin(a) * r);
			g.setNodeAttribute(m, "_community", c);
		});
	}

	// 5. Loose nodes: ring just outside a neighbour's community, unique
	//    golden angle each; else spiral on the periphery.
	let loose = 0;
	g.forEachNode((id) => {
		if (communityOf.has(id)) return;
		g.setNodeAttribute(id, "_community", null);
		let placed = false;
		for (const nb of g.neighbors(id)) {
			const c = communityOf.get(nb);
			if (!c) continue;
			const cx = meta.getNodeAttribute(c, "x") as number;
			const cy = meta.getNodeAttribute(c, "y") as number;
			const ring = radiusFor(groups.get(c)?.length ?? 0) + S;
			const a = loose * GOLDEN_ANGLE;
			g.setNodeAttribute(id, "x", cx + Math.cos(a) * ring);
			g.setNodeAttribute(id, "y", cy + Math.sin(a) * ring);
			placed = true;
			break;
		}
		if (!placed) {
			const a = loose * GOLDEN_ANGLE;
			const r = seedR + S * Math.sqrt(loose + 1);
			g.setNodeAttribute(id, "x", Math.cos(a) * r);
			g.setNodeAttribute(id, "y", Math.sin(a) * r);
		}
		loose++;
	});
}
